import React, { useMemo } from "react";
import { Table, Tag, Button, Space, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Voucher } from "../../models/voucher";
import { EVoucher } from "../../models/evoucher";
import { CampaignTypeEnum } from "../../constants/enums";
import dayjs from "dayjs";

const { Title } = Typography;

type Status = "ACTIVE" | "INACTIVE" | "EXPIRED";

type Props = {
  voucherData?: Voucher[];
  eVoucherData?: EVoucher[];

  selectedType?: CampaignTypeEnum;
  loading?: boolean;

  selectedKey?: React.Key | null;
  selectedKeys?: React.Key[];
  onChangeSelected?: (key: React.Key | null, row: any | null) => void;
  onAddSelect?: (row: any) => void;
  onSelectionChange?: (keys: React.Key[], rows: EVoucher[]) => void;
  selectableOnlyActive?: boolean;
};

const STATUS_COLOR: Record<Status, string> = {
  ACTIVE: "green",
  INACTIVE: "default",
  EXPIRED: "red",
};

const StatusTag = ({ value }: { value: unknown }) => {
  const text = String(value ?? "").toUpperCase();
  const color = STATUS_COLOR[text as Status] ?? "default";
  return (
    <Tag color={color} style={{ fontWeight: 600 }}>
      {text}
    </Tag>
  );
};

const isRowActive = (row: any) =>
  String(row?.StatusVoucherLabel ?? "").toLowerCase() === "active";

const buildEditUrl = (type: CampaignTypeEnum, id: React.Key) =>
  type === CampaignTypeEnum.Voucher || type === CampaignTypeEnum.VoucherPoint
    ? `${process.env.REACT_APP_DASHBOARD_URL || ""}/voucher/edit/${id}`
    : `${process.env.REACT_APP_DASHBOARD_GETZPASS_URL || ""}/Product#/e-voucher/edit/${id}`;

const formatUtc = (value?: string | Date | null) => {
  if (!value) return "—";
  const d = new Date(value);
  return d.toLocaleString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
};

/** Columns for VOUCHER */
const getVoucherColumns = (
  selectedType: CampaignTypeEnum,
  onChangeSelected?: Props["onChangeSelected"],
  onAddSelect?: Props["onAddSelect"],
  selectableOnlyActive: boolean = true,
): ColumnsType<Voucher> => [
  { title: "Getzpay Account Email", dataIndex: "Merchant", key: "merchant" },
  { title: "Voucher Name", dataIndex: "Name", key: "voucherName" },
  {
    title: "Voucher Type",
    dataIndex: "TypeVoucherMemberLabel",
    key: "voucherType",
  },
  {
    title: "Discount / Reward",
    dataIndex: "DiscountLabel",
    key: "discountLabel",
  },
  {
    title: "Status",
    dataIndex: "StatusVoucherLabel",
    key: "status",
    render: (status: unknown) => <StatusTag value={status} />,
  },
  {
    title: "Redemption Valid From",
    dataIndex: "StartDateFormated",
    key: "startDateFormated",
  },
  {
    title: "Redemption Valid Until",
    dataIndex: "EndDateFormated",
    key: "endDateFormated",
  },
  {
    title: "Action",
    key: "action",
    fixed: "right",
    render: (_, row) => {
      const disabled = selectableOnlyActive ? !isRowActive(row) : false;
      const editUrl = buildEditUrl(selectedType, row.Id);
      return (
        <Space>
          <a href={editUrl} target="_blank" rel="noopener noreferrer">
            <Button size="middle">Edit</Button>
          </a>
          <Button
            size="middle"
            type="primary"
            disabled={disabled}
            onClick={() => {
              onChangeSelected?.(row.Id, row);
              onAddSelect?.(row);
            }}
          >
            Add
          </Button>
        </Space>
      );
    },
  },
];

/** Columns for E-VOUCHER */
const getEVoucherColumns = (
  selectedType: CampaignTypeEnum,
): ColumnsType<EVoucher> => [
  { title: "E-Voucher Name", dataIndex: "Name", key: "voucherName" },
  {
    title: "Outlet",
    dataIndex: "Outlet",
    key: "outlet",
  },
  {
    title: "Selling Price",
    dataIndex: "Price",
    key: "price",
  },
  {
    title: "Selling Period",
    key: "sellingPeriod",
    render: (_, row) => {
      if (!row.StartDate || !row.EndDate) return "—";

      const start = dayjs(row.StartDate);
      const end = dayjs(row.EndDate);

      const dateRange = `${start.format("DD MMM YYYY")} - ${end.format(
        "DD MMM YYYY",
      )}`;
      const timeRange = `${start.format("HH:mm")} - ${end.format("HH:mm")}`;

      return (
        <div>
          <div>{dateRange}</div>
          <div>{timeRange}</div>
        </div>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "Status",
    key: "status",
    render: (status: unknown) => <StatusTag value={status} />,
  },
  {
    title: "Action",
    key: "action",
    fixed: "right",
    render: (_, row) => {
      const editUrl = buildEditUrl(selectedType, row.Id);
      return (
        <a href={editUrl} target="_blank" rel="noopener noreferrer">
          <Button size="middle">Edit</Button>
        </a>
      );
    },
  },
];

export default function VoucherTable({
  voucherData = [],
  eVoucherData = [],
  selectedType,
  loading = false,
  selectedKey,
  selectedKeys,
  onChangeSelected,
  onAddSelect,
  onSelectionChange,
  selectableOnlyActive = true,
}: Props) {
  const safeType = selectedType ?? CampaignTypeEnum.Voucher;

  const voucherColumns = useMemo(
    () =>
      getVoucherColumns(
        safeType,
        onChangeSelected,
        onAddSelect,
        selectableOnlyActive,
      ),
    [safeType, onChangeSelected, onAddSelect, selectableOnlyActive],
  );

  const eVoucherColumns = useMemo(() => getEVoucherColumns(safeType), [safeType]);

  const eVoucherRowSelection = selectedKeys
    ? {
        type: "checkbox" as const,
        selectedRowKeys: selectedKeys,
        onChange: (keys: React.Key[], rows: EVoucher[]) =>
          onSelectionChange?.(keys, rows),
      }
    : undefined;

  const showOnlyVoucher =
    selectedType === CampaignTypeEnum.Voucher ||
    selectedType === CampaignTypeEnum.VoucherPoint;
  const showOnlyEVoucher = selectedType === CampaignTypeEnum.EVoucher;
  const showBoth =
    !selectedType || selectedType === CampaignTypeEnum.VoucherPoint;

  if (showOnlyVoucher) {
    return (
      <Table<Voucher>
        columns={voucherColumns}
        dataSource={voucherData}
        rowKey="Id"
        bordered
        scroll={{ x: "max-content" }}
        loading={loading}
        pagination={{
          defaultPageSize: 5,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "15", "20"],
        }}
      />
    );
  }

  if (showOnlyEVoucher) {
    return (
      <Table<EVoucher>
        columns={eVoucherColumns}
        dataSource={eVoucherData}
        rowKey="Id"
        rowSelection={eVoucherRowSelection}
        bordered
        scroll={{ x: "max-content" }}
        loading={loading}
        pagination={{
          defaultPageSize: 5,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "15", "20"],
        }}
      />
    );
  }

  // Combined view
  return (
    <>
      <Title level={5} style={{ marginTop: 0 }}>
        Voucher
      </Title>
      <Table<Voucher>
        columns={voucherColumns}
        dataSource={voucherData}
        rowKey="Id"
        bordered
        scroll={{ x: "max-content" }}
        loading={loading}
        pagination={{
          defaultPageSize: 5,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "15", "20"],
        }}
      />

      <Title level={5}>E-Voucher</Title>
      <Table<EVoucher>
        columns={eVoucherColumns}
        dataSource={eVoucherData}
        rowKey="Id"
        rowSelection={eVoucherRowSelection}
        bordered
        scroll={{ x: "max-content" }}
        loading={loading}
        pagination={{
          defaultPageSize: 5,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "15", "20"],
        }}
      />
    </>
  );
}
